import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { y: 48, opacity: 0 },
  whileInView: { y: 0, opacity: 1 },
  viewport: { once: true, margin: "-12% 0px" },
  transition: { duration: 0.9, ease: EASE, delay },
});

export default function DepthChapter({
  id,
  index,
  zone,
  range,
  title,
  fact,
  copy,
  image,
  imageAlt,
  capabilities = [],
  flip = false,
  wide = false,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-9%", "9%"]);

  return (
    <section
      id={id}
      ref={ref}
      data-testid={`chapter-${index}`}
      className="relative px-6 py-32 text-[#F8FAFC] md:px-12 md:py-48"
    >
      <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-8">
        <motion.div {...reveal(0)} className="md:col-span-2">
          <span className="chapter-index" data-testid={`chapter-index-${index}`}>
            {index}
          </span>
          <span className="mt-5 block h-px w-16 bg-[#f58220]" />
        </motion.div>

        <div className={wide ? "md:col-span-10" : "md:col-span-6"}>
          <motion.h2
            {...reveal(0.12)}
            className="font-display text-4xl font-light leading-[1.02] tracking-tighter md:text-6xl"
          >
            {title}
          </motion.h2>
          {!image && (
            <motion.blockquote
              {...reveal(0.2)}
              className="mt-8 max-w-xl font-editorial text-base italic leading-snug text-[#F8FAFC]/70 md:text-lg"
            >
              “{fact}”
            </motion.blockquote>
          )}
          <motion.p
            {...reveal(0.28)}
            className="mt-6 max-w-lg text-base leading-relaxed text-[#F8FAFC]/60 md:text-lg"
          >
            {copy}
          </motion.p>
          {capabilities.length > 0 && (
            <motion.ul
              {...reveal(0.34)}
              className="mt-8 flex max-w-2xl flex-wrap gap-2"
              data-testid={`chapter-caps-${index}`}
            >
              {capabilities.map((cap) => (
                <li
                  key={cap}
                  className="rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#F8FAFC]/55 transition-colors duration-300 hover:border-[#26c6d3]/60 hover:text-[#26c6d3] md:text-[11px]"
                >
                  {cap}
                </li>
              ))}
            </motion.ul>
          )}
        </div>

        {!wide && image && (
          <motion.div
            {...reveal(0.15)}
            className={`md:col-span-4 ${flip ? "md:order-first" : ""}`}
          >
            <div
              className="group relative overflow-hidden"
              data-testid={`chapter-image-${index}`}
            >
              <motion.div style={{ y: imgY }}>
                <img
                  src={image}
                  alt={imageAlt}
                  loading="lazy"
                  className="h-[46vh] w-full scale-[1.15] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.22] md:h-[62vh]"
                />
              </motion.div>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#020B14]/55 via-transparent to-transparent" />
            </div>
            <motion.blockquote
              {...reveal(0.25)}
              className="mt-4 font-editorial text-base italic leading-snug text-[#F8FAFC]/60 md:text-lg"
              data-testid={`chapter-fact-${index}`}
            >
              “{fact}”
            </motion.blockquote>
          </motion.div>
        )}
      </div>
    </section>
  );
}
